import '../../i18n/autoTranslate';
import React, { useMemo } from 'react';
import SpotlightCard from '../../../components/ui/SpotlightCard';
import Icon from '../../../components/AppIcon';

const safeNum = (v, d = 0) => (Number.isFinite(Number(v)) ? Number(v) : d);
const safeScore = (it, d = null) => {
  const n = Number(it?.performance_score ?? it?.overall_score ?? it?.final_score ?? it?.score);
  return Number.isFinite(n) ? n : d;
};
const pretty = (v) => String(v || '—').replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const meta = (it) => {
  const cfg = it?.configuration || it?.config || {};
  return {
    role: it?.selected_role || cfg?.selected_role || it?.role || 'Untitled role',
    industry: it?.selected_industry || cfg?.selected_industry || it?.industry || '—',
    type: it?.interview_type || cfg?.interview_type || it?.type || '—',
    format: it?.selected_format || cfg?.selected_format || it?.format || '—',
    date: it?.completed_at || it?.created_at || it?.updated_at,
  };
};

const typeStyle = (type) => {
  switch (type) {
    case 'positive':
      return { pill: 'border-emerald-400/20 bg-emerald-400/10 text-emerald-100', icon: 'Sparkles' };
    case 'warning':
      return { pill: 'border-amber-300/25 bg-amber-300/10 text-amber-100', icon: 'TriangleAlert' };
    case 'negative':
      return { pill: 'border-rose-400/20 bg-rose-400/10 text-rose-100', icon: 'AlertTriangle' };
    default:
      return { pill: 'border-white/12 bg-white/6 text-white/80', icon: 'Info' };
  }
};

const AIPoweredInsights = ({ insights = [], interviews = [], statistics = null }) => {
  const normalized = useMemo(() => {
    const base = Array.isArray(insights) ? insights : [];
    const list = base.map((x, idx) => ({
      id: `${x.title || 'ins'}_${idx}`,
      type: x.type || 'neutral',
      title: x.title || 'Insight',
      description: x.description || '',
      priority: x.priority || 'medium',
      icon: x.icon,
    }));

    // If nothing was generated, produce a minimal helpful set
    if (list.length) return list;

    const avg = safeNum(statistics?.average_score, 0);
    const best = safeNum(statistics?.highest_score, 0);
    const completed = safeNum(statistics?.completed_interviews, 0);

    const out = [];
    out.push({
      id: 'ins_1',
      type: 'neutral',
      title: 'What we measure',
      description: 'We track score trends, format mix, skill categories, and session feedback to generate an improvement plan.',
      priority: 'low',
      icon: 'BarChart3',
    });

    if (completed < 3) {
      out.push({
        id: 'ins_2',
        type: 'warning',
        title: 'Complete a few sessions first',
        description: 'More sessions → better signals. Aim for 3–5 completed sessions to unlock accurate skill insights.',
        priority: 'high',
        icon: 'Target',
      });
    } else if (avg >= 85) {
      out.push({
        id: 'ins_3',
        type: 'positive',
        title: 'Strong baseline',
        description: `Your average score is ${Math.round(avg)}. Focus on consistency and tightening weak areas.`,
        priority: 'high',
        icon: 'TrendingUp',
      });
    } else {
      out.push({
        id: 'ins_3',
        type: 'warning',
        title: 'Raise your baseline',
        description: 'Improve structure + clarity first. It produces the biggest score jump across formats.',
        priority: 'high',
        icon: 'MessageSquareText',
      });
    }

    if (best >= 90) {
      out.push({
        id: 'ins_4',
        type: 'positive',
        title: 'Peak potential proven',
        description: 'You’ve hit 90+. Now reduce score variance with repetition and pacing.',
        priority: 'medium',
        icon: 'Sparkles',
      });
    }

    return out;
  }, [insights, statistics]);

  const recentSessions = useMemo(() => {
    return [...(interviews || [])]
      .filter((it) => String(it?.status || '').toLowerCase() === 'completed')
      .sort((a, b) => new Date(meta(b).date || 0) - new Date(meta(a).date || 0))
      .slice(0, 5)
      .map((it) => ({ ...meta(it), score: safeScore(it), id: it?.id }));
  }, [interviews]);

  const grouped = useMemo(() => {
    const score = (p) => (p === 'high' ? 3 : p === 'medium' ? 2 : 1);
    const sorted = [...normalized].sort((a, b) => score(b.priority) - score(a.priority));
    return {
      top: sorted.slice(0, 4),
      rest: sorted.slice(4),
    };
  }, [normalized]);

  const quickActions = [
    { title: 'Answer format', desc: 'Summary → 2–3 points → example → impact.', icon: 'ListChecks' },
    { title: 'Follow-up handling', desc: 'Clarify constraints, think aloud, confirm final answer.', icon: 'Brain' },
    { title: 'Consistency loop', desc: '1 short drill/day beats 1 long session/week.', icon: 'Calendar' },
  ];

  return (
    <SpotlightCard
      className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl shadow-[0_18px_50px_-22px_rgba(0,0,0,0.75)]"
      spotlightColor="rgba(52,211,153,0.12)"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 left-1/2 h-72 w-[48rem] -translate-x-1/2 rounded-full bg-gradient-to-r from-violet-500/16 via-sky-500/14 to-emerald-500/14 blur-3xl" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/10 to-black/45" />
      </div>

      <div className="relative z-10 p-6">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <div className="text-sm font-semibold text-white">AI-powered insights</div>
            <div className="text-xs text-white/55 mt-1">
              A professional readout of what’s working, what’s missing, and what to do next.
            </div>
          </div>

          <div className="flex items-center gap-2 text-[11px] text-white/55">
            <span className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1">Evidence-based</span>
            <span className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1">Actionable</span>
            <span className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1">Session-level</span>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            {grouped.top.map((ins) => {
              const st = typeStyle(ins.type);
              return (
                <div key={ins.id} className="rounded-3xl border border-white/10 bg-white/[0.03] p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold backdrop-blur-xl " style={{}}>
                        <span className={`rounded-full border px-2.5 py-0.5 ${st.pill}`}>
                          <span className="inline-flex items-center gap-1.5">
                            <Icon name={ins.icon || st.icon} size={14} />
                            {ins.priority.toUpperCase()}
                          </span>
                        </span>
                      </div>

                      <div className="mt-3 text-base font-semibold text-white">{ins.title}</div>
                      <div className="mt-1 text-sm text-white/65 leading-relaxed">{ins.description}</div>
                    </div>
                  </div>
                </div>
              );
            })}

            {grouped.rest.length ? (
              <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5">
                <div className="text-sm font-semibold text-white">More signals</div>
                <div className="mt-3 space-y-2">
                  {grouped.rest.slice(0, 6).map((ins) => (
                    <div key={ins.id} className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.02] p-3">
                      <div className="mt-0.5 h-8 w-8 rounded-xl border border-white/10 bg-white/[0.03] flex items-center justify-center">
                        <Icon name={ins.icon || typeStyle(ins.type).icon} size={14} className="text-white/70" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-semibold text-white/80">{ins.title}</div>
                        <div className="text-xs text-white/55 mt-1">{ins.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-white">Past-interview evidence</div>
                  <div className="text-xs text-white/55 mt-1">Recent completed sessions feeding these insights.</div>
                </div>
                <Icon name="History" size={18} className="text-white/60" />
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                {recentSessions.length ? recentSessions.map((s) => (
                  <div key={s.id || `${s.role}-${s.date}`} className="rounded-2xl border border-white/10 bg-white/[0.025] p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-white truncate">{s.role}</div>
                        <div className="mt-1 text-xs text-white/55 truncate">
                          {s.industry} • {pretty(s.format)} • {pretty(s.type)}
                        </div>
                      </div>
                      <div className="shrink-0 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-1 text-sm font-semibold text-white">
                        {s.score == null ? '—' : Math.round(s.score)}
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className="text-sm text-white/55">No completed interviews yet.</div>
                )}
              </div>
            </div>
          </div>

          <div className="lg:col-span-1 space-y-6">
            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5">
              <div className="text-sm font-semibold text-white">Recommended answer format</div>
              <div className="text-xs text-white/55 mt-1">Use this to improve clarity and scoring.</div>

              <div className="mt-4 space-y-3">
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3">
                  <div className="text-xs font-semibold text-white/80">1) Summary</div>
                  <div className="text-xs text-white/55 mt-1">One sentence: what you did / decided / built.</div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3">
                  <div className="text-xs font-semibold text-white/80">2) Key points</div>
                  <div className="text-xs text-white/55 mt-1">2–3 bullets: constraints, approach, trade-offs.</div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3">
                  <div className="text-xs font-semibold text-white/80">3) Evidence</div>
                  <div className="text-xs text-white/55 mt-1">A concrete example + measurable impact.</div>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5">
              <div className="text-sm font-semibold text-white">Quick actions</div>
              <div className="text-xs text-white/55 mt-1">Small moves with big impact.</div>

              <div className="mt-4 space-y-3">
                {quickActions.map((a) => (
                  <div key={a.title} className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.02] p-3">
                    <div className="mt-0.5 h-8 w-8 rounded-xl border border-white/10 bg-white/[0.03] flex items-center justify-center">
                      <Icon name={a.icon} size={14} className="text-white/70" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-white/80">{a.title}</div>
                      <div className="text-xs text-white/55 mt-1">{a.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5">
              <div className="text-sm font-semibold text-white">What you get</div>
              <div className="mt-3 text-xs text-white/60 space-y-2">
                <div className="flex items-start gap-2"><span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-emerald-300/80" /> Score trend + volume</div>
                <div className="flex items-start gap-2"><span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-sky-300/80" /> Skill radar + category breakdown</div>
                <div className="flex items-start gap-2"><span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-violet-300/80" /> Session-level drilldown with feedback</div>
                <div className="flex items-start gap-2"><span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-amber-300/80" /> Weekly plan & goals</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SpotlightCard>
  );
};

export default AIPoweredInsights;
